/**
 * SecurityTab — org_admin+ page for the per-org IP allowlist.
 *
 * Lets an admin toggle the gate on/off and maintain a list of CIDR strings
 * (or plain IPs, which the backend normalises to /32 or /128). When enabled,
 * the backend `IpAllowlistMiddleware` rejects authenticated requests from
 * outside the listed ranges.
 *
 * Public Lumyn Live participant endpoints (`/api/v1/live/*`) are always
 * exempt by design — floor briefing on cellular data must keep working.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Shield, Plus, X, Globe } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const HINT_TEXT =
  'Accepts a single IPv4/IPv6 (e.g. 203.0.113.42) or a CIDR block (e.g. 203.0.113.0/24, 2001:db8::/32). One per row.';

function isPlausibleCidr(value) {
  // Cheap client-side sanity check; the backend is the source of truth.
  if (!value || typeof value !== 'string') return false;
  const v = value.trim();
  // IPv6 contains colons; IPv4 contains dots and digits.
  return /^[0-9a-fA-F:.]+(\/\d{1,3})?$/.test(v);
}

export default function SecurityTab() {
  const { get, put } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState([]); // [{value: string}]
  const [draft, setDraft] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    get('/organizations/current/settings')
      .then((s) => {
        if (!active) return;
        setEnabled(!!s.ip_allowlist_enabled);
        setRules((s.ip_allowlist || []).map((v) => ({ value: v })));
      })
      .catch(() => toast.error('Could not load security settings'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [get]);

  const addRule = () => {
    const v = draft.trim();
    if (!v) return;
    if (!isPlausibleCidr(v)) {
      toast.error('That does not look like an IP or CIDR block.');
      return;
    }
    if (rules.some((r) => r.value === v)) {
      toast.error('That entry is already in the list.');
      return;
    }
    setRules([...rules, { value: v }]);
    setDraft('');
  };

  const removeRule = (idx) => {
    setRules(rules.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      await put('/organizations/current/settings', {
        ip_allowlist_enabled: enabled,
        ip_allowlist: rules.map((r) => r.value),
      });
      toast.success('Security settings saved');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="security-tab">
      <section className="rounded-xl border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[var(--lumyn-settings)]" />
              <h2 className="font-display text-lg font-semibold tracking-tight">
                IP allowlist
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Restrict authenticated access to a fixed list of office or VPN
              ranges. Live-session participants are always exempt so floor
              briefings on mobile data keep working.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="ip-allowlist-toggle"
            />
            <span className="text-sm font-medium">{enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Allowed ranges</Label>
          {rules.length === 0 ? (
            <div className="text-xs italic text-muted-foreground py-3 px-2 border rounded bg-muted/30">
              No rules yet. Add at least one before enabling the gate, or
              you&apos;ll lock yourself out.
            </div>
          ) : (
            <ul className="space-y-1" data-testid="ip-allowlist-list">
              {rules.map((r, idx) => (
                <li
                  key={`${r.value}-${idx}`}
                  className="flex items-center justify-between gap-2 border rounded px-3 py-2 text-sm bg-background"
                  data-testid={`ip-rule-${idx}`}
                >
                  <span className="font-mono inline-flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                    {r.value}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRule(idx)}
                    className="text-muted-foreground hover:text-rose-500 transition"
                    data-testid={`ip-rule-remove-${idx}`}
                    aria-label="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2 pt-1">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. 203.0.113.0/24"
              className="font-mono"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addRule();
                }
              }}
              data-testid="ip-rule-draft"
            />
            <Button
              type="button"
              variant="outline"
              onClick={addRule}
              data-testid="ip-rule-add"
            >
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">{HINT_TEXT}</p>
        </div>

        <div className="flex justify-end pt-2 border-t">
          <Button
            onClick={save}
            disabled={saving || (enabled && rules.length === 0)}
            data-testid="security-save"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Save
          </Button>
        </div>

        {enabled && rules.length > 0 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            ⚠ Heads up: this gate applies to all authenticated routes for your
            org, including admin tools. Make sure your own current IP is in
            the list before you save.
          </div>
        )}
      </section>
    </div>
  );
}
